import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AiOutlineDownload, AiOutlinePrinter } from 'react-icons/ai';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomerNotesService from '../../services/CustomerNotesService';
import './CustomerDetailsModal.css';

export default function CustomerDetailsModal({ show, onHide, customer }) {
  const [showFileNamePrompt, setShowFileNamePrompt] = useState(false);
  const [pendingExportType, setPendingExportType] = useState(null); // 'pdf' or 'print'
  const [customFileName, setCustomFileName] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [userContext, setUserContext] = useState({
    email: '',
    role: 'user',
  });

  const customerId = useMemo(() => {
    if (!customer) return null;
    return customer.customer_id ?? customer.ID ?? null;
  }, [customer]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('userData');
      if (stored) {
        const parsed = JSON.parse(stored);
        setUserContext({
          email: parsed?.email ?? '',
          role: parsed?.role ?? 'user',
        });
      }
    } catch (error) {
      console.error('Failed to parse user data from storage:', error);
    }
  }, []);

  const loadNotes = useCallback(async () => {
    if (!customerId) return;
    setNotesLoading(true);
    setNotesError('');
    try {
      const { data } = await CustomerNotesService.getAll(customerId);
      setNotes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customer notes:', error);
      setNotesError('Failed to load notes. Please try again.');
    } finally {
      setNotesLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!show || !customerId) return;
    setActiveTab('details');
    loadNotes();
  }, [show, customerId, loadNotes]);

  useEffect(() => {
    if (!show) {
      setNoteContent('');
      setEditingNoteId(null);
      setActiveTab('details');
    }
  }, [show]);

  const canModifyNote = useCallback((note) => {
    if (!note) return false;
    if (userContext.role === 'admin') return true;
    if (!userContext.email) return false;
    return note.author?.email && note.author.email.toLowerCase() === userContext.email.toLowerCase();
  }, [userContext]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleNoteSubmit = async (event) => {
    event.preventDefault();
    if (!customerId) {
      toast.error('Customer is missing. Please close and reopen.', { autoClose: 2500 });
      return;
    }
    const trimmed = noteContent.trim();
    if (!trimmed) {
      toast.error('Please enter a note before saving.', { autoClose: 2500 });
      return;
    }

    setIsSavingNote(true);
    try {
      if (editingNoteId) {
        const { data } = await CustomerNotesService.update(customerId, editingNoteId, { content: trimmed });
        setNotes((prev) =>
          prev.map((note) => (note.note_id === data.note_id ? data : note))
        );
        toast.success('Note updated successfully.', { autoClose: 2000 });
      } else {
        const { data } = await CustomerNotesService.create(customerId, { content: trimmed });
        setNotes((prev) => [data, ...prev]);
        toast.success('Note added successfully.', { autoClose: 2000 });
      }
      setNoteContent('');
      setEditingNoteId(null);
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to save note.';
      toast.error(message, { autoClose: 2500 });
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.note_id);
    setNoteContent(note.content);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setNoteContent('');
  };

  const handleDeleteNote = async (note) => {
    if (!customerId) return;
    if (!canModifyNote(note)) {
      toast.error('You are not allowed to delete this note.', { autoClose: 2500 });
      return;
    }

    const confirmDelete = window.confirm('Are you sure you want to delete this note?');
    if (!confirmDelete) return;

    try {
      await CustomerNotesService.remove(customerId, note.note_id);
      setNotes((prev) => prev.filter((item) => item.note_id !== note.note_id));
      toast.success('Note deleted.', { autoClose: 2000 });
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to delete note.';
      toast.error(message, { autoClose: 2500 });
    }
  };

  const renderAuthor = (note) => {
    if (!note?.author) {
      return 'System';
    }
    const { firstName, lastName, userName, email } = note.author;
    if (firstName || lastName) {
      return `${firstName ?? ''} ${lastName ?? ''}`.trim();
    }
    return userName || email || 'Unknown';
  };

  const renderNoteTimestamp = (note) => {
    if (!note?.createdAt) return '';
    const date = new Date(note.createdAt);
    return date.toLocaleString();
  };

  if (!show || !customer) return null;

  // Normalize fields including snake_case
  const id        = customer.customer_id ?? customer.ID ?? '';
  const firstName = customer.firstName ?? customer['First Name'] ?? '';
  const lastName  = customer.lastName  ?? customer['Last Name'] ?? '';
  const email     = customer.email     ?? customer.Email          ?? '';
  const phone     = customer.phone     ?? customer.Phone          ?? '';
  const address   = customer.address   ?? customer.Address        ?? '';

  // File name prompt logic
  const handleExportWithPrompt = (type) => {
    setPendingExportType(type);
    setCustomFileName('');
    setShowFileNamePrompt(true);
  };

  const handleFileNameSubmit = (e) => {
    e.preventDefault();
    let name = customFileName.trim();
    if (!name) name = `customer_${id}`;
    if (pendingExportType === 'pdf' && !name.endsWith('.pdf')) name += '.pdf';
    setShowFileNamePrompt(false);
    if (pendingExportType === 'pdf') handleSavePDF(name);
    if (pendingExportType === 'print') handlePrint(name);
  };

  // PDF export
  const handleSavePDF = (customName) => {
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
      const margin = 40;
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(20);
      doc.setTextColor(16, 39, 123);
      doc.text('Customer Details', margin, margin);
      const rows = [
        ['ID', id],
        ['Name', `${firstName} ${lastName}`],
        ['Email', email],
        ['Phone', phone],
        ['Address', address]
      ];
      autoTable(doc, {
        startY: margin + 30,
        margin: { left: margin, right: margin },
        head: [['Field', 'Value']],
        body: rows,
        theme: 'grid',
        styles: { fontSize: 12, cellPadding: 6 },
        headStyles: { fillColor: [16, 39, 123], textColor: 255 },
        columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: pageWidth - margin * 2 - 100 } }
      });
      doc.save(customName);
      toast.success('PDF saved successfully!', { autoClose: 2000 });
    } catch (error) {
      toast.error('Error generating PDF. Please try again.', { autoClose: 3000 });
    }
  };

  // Print export
  const handlePrint = () => {
    // Open a new window with printable content
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Customer Details</title>
            <style>
              body { font-family: 'Inter', Arial, sans-serif; margin: 40px; color: #10277B; }
              h2 { color: #10277B; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #10277B; padding: 10px; text-align: left; font-size: 16px; }
              th { background: #10277B; color: #fff; }
              tr:nth-child(even) { background: #f5f5f5; }
            </style>
          </head>
          <body>
            <h2>Customer Details</h2>
            <table>
              <tr><th>Field</th><th>Value</th></tr>
              <tr><td>ID</td><td>${id}</td></tr>
              <tr><td>Name</td><td>${firstName} ${lastName}</td></tr>
              <tr><td>Email</td><td>${email}</td></tr>
              <tr><td>Phone</td><td>${phone}</td></tr>
              <tr><td>Address</td><td>${address}</td></tr>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      toast.success('Print dialog opened!', { autoClose: 2000 });
    } else {
      toast.error('Unable to open print dialog.', { autoClose: 3000 });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md" centered backdrop="static">
      <Modal.Header className="modal-header-custom">
        <Modal.Title className="modal-title-custom">Customer Details</Modal.Title>
        <button type="button" className="btn-close" onClick={onHide} />
      </Modal.Header>
      <div className="modal-actions-row right-actions">
        <AiOutlineDownload className="action-icon" title="Download PDF" onClick={() => handleExportWithPrompt('pdf')} />
        <AiOutlinePrinter className="action-icon" title="Print" onClick={() => handleExportWithPrompt('print')} />
      </div>
      <Modal.Body className="modal-body-custom">
        <div className="details-tabs">
          <button
            type="button"
            className={`details-tab-button ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => handleTabChange('details')}
          >
            Overview
          </button>
          <button
            type="button"
            className={`details-tab-button ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => handleTabChange('notes')}
          >
            Notes
          </button>
        </div>

        {activeTab === 'details' && (
        <div className="customer-details-card">
          <div className="details-row">
            <div className="details-label">ID:</div>
            <div className="details-value">{id}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Name:</div>
            <div className="details-value">{firstName} {lastName}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Email:</div>
            <div className="details-value">{email ? <a href={`mailto:${email}`} className="link">{email}</a> : 'N/A'}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Phone:</div>
            <div className="details-value">{phone ? <a href={`tel:${phone}`} className="link">{phone}</a> : 'N/A'}</div>
          </div>
          <div className="details-row">
            <div className="details-label">Address:</div>
            <div className="details-value">{address || 'N/A'}</div>
          </div>
        </div>
        )}

        {activeTab === 'notes' && (
          <div className="customer-notes-card">
            <form className="note-editor" onSubmit={handleNoteSubmit}>
              <label htmlFor="customer-note-content" className="note-label">
                {editingNoteId ? 'Edit note' : 'Add a new note'}
              </label>
              <textarea
                id="customer-note-content"
                className="note-textarea"
                value={noteContent}
                onChange={(event) => setNoteContent(event.target.value)}
                placeholder="Type your note here..."
                rows={4}
              />
              <div className="note-actions">
                {editingNoteId && (
                  <button
                    type="button"
                    className="note-button secondary"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="note-button primary"
                  disabled={isSavingNote}
                >
                  {isSavingNote ? 'Saving...' : editingNoteId ? 'Update Note' : 'Add Note'}
                </button>
              </div>
            </form>

            <div className="notes-list-wrapper">
              {notesLoading && (
                <div className="notes-placeholder">Loading notes...</div>
              )}
              {!notesLoading && notesError && (
                <div className="notes-error">
                  {notesError}
                  <button type="button" className="note-button link" onClick={loadNotes}>
                    Try again
                  </button>
                </div>
              )}
              {!notesLoading && !notesError && notes.length === 0 && (
                <div className="notes-placeholder">No notes yet. Start by adding one above.</div>
              )}
              {!notesLoading && !notesError && notes.length > 0 && (
                <ul className="notes-list">
                  {notes.map((note) => (
                    <li key={note.note_id} className="note-item">
                      <div className="note-header">
                        <span className="note-author">{renderAuthor(note)}</span>
                      </div>
                      <p className="note-content">{note.content}</p>
                      <div className="note-footer">
                        <span className="note-timestamp">{renderNoteTimestamp(note)}</span>
                      </div>
                      {canModifyNote(note) && (
                        <div className="note-item-actions">
                          <button
                            type="button"
                            className="note-icon-button"
                            onClick={() => handleEditNote(note)}
                            title="Edit note"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className="note-icon-button delete"
                            onClick={() => handleDeleteNote(note)}
                            title="Delete note"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      {/* File name prompt modal */}
      {showFileNamePrompt && (
        <div className="file-name-prompt-backdrop">
          <div className="file-name-prompt-modal pretty-modal">
            <form onSubmit={handleFileNameSubmit}>
              <label htmlFor="exportFileName" className="pretty-label">Enter file name:</label>
              <input
                id="exportFileName"
                type="text"
                value={customFileName}
                onChange={e => setCustomFileName(e.target.value)}
                autoFocus
                placeholder={pendingExportType === 'pdf' ? `customer_${id}.pdf` : `customer_${id}`}
                className="file-name-input pretty-input"
              />
              <div className="file-name-prompt-actions pretty-actions">
                <button type="submit" className="btn-save pretty-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Modal>
  );
}
